import { helper } from '@ember/component/helper';
import { get } from '@ember/object';
import { isEmpty } from '@ember/utils';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import type Value from '@linc-technologies/linced-common/models/dynamic-field/value';
import {
  flattenList,
  type FlattenField,
} from '@linc-technologies/linced-common/utils/fields';

// field-value gives the display value of a dynamic field on a record.
//
// - record: a record or fragment with dynamic fields.
// - key: the field key, for example `core:firstName`.
// - field (optional): the field definition. Its options change STRING,
//   INTEGER and LIST values to their labels.
//
// The helper reads the field through tracked state, so it updates when the
// value changes or when the record gets the field.
//
// Usage: {{field-value this.person "custom:house" this.houseField}}
export default helper(function fieldValue([record, key, field]: [
  object | null | undefined,
  string,
  (FlattenField | null)?,
]): string | number | boolean | null | undefined {
  if (isEmpty(record) || !key) {
    return null;
  }

  const fieldValue = get(record, `fields.${key}`) as Value | undefined;
  const value = fieldValue?.value;
  if (isEmpty(value)) {
    return;
  }

  switch (fieldValue!.datatype) {
    case DataType.STRING:
    case DataType.INTEGER:
      // With a field definition, show the option label.
      return field ? flattenList([value], field) : (value as string | number);
    case DataType.LIST:
      return flattenList(value as StatefulArray<string>, field);
    case DataType.BOOLEAN:
      return value ? 'Yes' : 'No';
  }
  // FLOAT and HTML. A VALUE_LIST shows as its text, as before.
  return value as string | number;
});
